import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";

export const ProductPage = () => {
  const { artworkId } = useParams();
  const { addToCart } = useCart();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchArtwork = async () => {
      try {
        const response = await axios.get(`${API}/artworks/${artworkId}`);
        setArtwork(response.data);
      } catch (e) {
        console.error("Error fetching artwork:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArtwork();
  }, [artworkId]);

  const handleAddToCart = async () => {
    setAdding(true);
    const success = await addToCart({
      artwork_id: artwork.id,
      title: artwork.title,
      price: artwork.price,
      image_url: artwork.image_url,
      quantity: 1,
    });

    if (success) {
      toast.success(`${artwork.title} added to cart`);
    } else {
      toast.error("Failed to add to cart");
    }
    setAdding(false);
  };

  const seasonColors = {
    spring: "bg-spring",
    summer: "bg-summer",
    fall: "bg-fall",
    winter: "bg-winter",
  };

  if (loading) {
    return (
      <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-[3/4] bg-muted animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted animate-pulse w-1/3" />
            <div className="h-12 bg-muted animate-pulse w-2/3" />
            <div className="h-24 bg-muted animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="py-24 text-center" data-testid="artwork-not-found">
        <p className="text-muted-foreground mb-4">Artwork not found</p>
        <Link to="/shop" className="text-primary hover:underline">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto" data-testid="product-page">
      <Link 
        to="/shop" 
        className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground hover:text-primary mb-12"
        data-testid="back-to-shop"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
        {/* Image */}
        <div className="relative">
          <img
            src={artwork.image_url}
            alt={artwork.title}
            className="w-full aspect-square object-contain bg-muted/20"
          />
          {artwork.is_featured && (
            <span className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-xs uppercase tracking-widest">
              Featured
            </span>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-col">
          <span className={`inline-block w-fit ${seasonColors[artwork.season]} px-3 py-1 text-xs uppercase tracking-widest text-foreground mb-4`}>
            {artwork.season} {artwork.year}
          </span>

          <h1 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-2" data-testid="artwork-title">
            {artwork.title}
          </h1>

          <p className="text-muted-foreground mb-6">By {artwork.artist}</p>

          <p className="text-2xl font-medium mb-8" data-testid="artwork-price">
            ${artwork.price.toFixed(2)}
          </p>

          <p className="text-muted-foreground leading-relaxed mb-8">
            {artwork.description}
          </p>

          <div className="border-t border-border pt-8 mb-8">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4">What You Get</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <Check className="w-4 h-4 text-green-600" />
                High-resolution digital download
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-4 h-4 text-green-600" />
                Instant delivery to your email
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-4 h-4 text-green-600" />
                Print-ready quality
              </li>
              <li className="flex items-center gap-3">
                <Check className="w-4 h-4 text-green-600" />
                Personal use license included
              </li>
            </ul>
          </div>

          <Button
            onClick={handleAddToCart}
            disabled={adding}
            className="w-full md:w-auto rounded-none px-12 py-6 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold gap-2"
            data-testid="add-to-cart-btn"
          >
            <ShoppingBag className="w-4 h-4" />
            {adding ? "Adding..." : "Add to Cart"}
          </Button>

          <p className="mt-4 text-xs text-muted-foreground">
            Digital product - No returns. Download link sent to your email after purchase.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
